import { ZButton } from "@/shared/components/button";
import ZBreadCrumbs from "@/shared/components/custom/breadcrumbs";
import { LoadingSpinner } from "@/shared/components/custom/spin";
import ErrorBoundary from "@/shared/components/error-boundary";
import {
  type ZTableRowParams
} from "@/shared/components/table-deprecrated/TableDeprecrated";
import { ROUTES } from "@/shared/constants/routes";
import useQuery from "@/shared/hooks/use-query";
import { addNotification } from "@/shared/states/notification";
import throttle from "@/shared/utils/throttle";
import {
  Button,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownTrigger,
  Input,
  Spinner,
  Tab,
  Tabs,
  cn
} from "@nextui-org/react";
/* eslint-disable @typescript-eslint/no-unsafe-enum-comparison */
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { ArrowRight, Download, Ellipsis } from "lucide-react";
import { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import {
  frameworkTypeLabels,
  searchPlaceholderMap
} from "../constants";
import { getAgentSession, getPaginatedAgentSessions } from "../requests";
import {
  type AgentSession,
  AgentSessionStatus,
  type AgentSessionStep,
  AgentSessionType,
  type AgentSessions,
  CustomGapAgentSession,
  type DoraAgentSession,
  FairAgentSession,
  type MultiVendorAgentSession,
  type QaAgentSession,
  type RiskAgentSession,
  type SOC2GapAgentSession,
  type SOC2Type1GapAgentSession,
  type SOC2Type2NewGapAgentSession
} from '../types';

import { handleContinueCustomizeControls } from "@/modules/agent/use-cases/customize-control.use-case";
import { generateAndDownloadSessionReport } from '@/modules/agent/utils/downloadReport';
import { useGetTeamsListQuery } from "@/modules/auth/requests";
import { useLoggedInMember } from "@/modules/auth/states";
import { ZTable } from "@/shared/components/table";
import { ZTablePagination } from "@/shared/components/TablePagination";
import { usePagination } from "@/shared/hooks/usePagination";
import { useSearch } from "@/shared/hooks/useSearch";
import { useSort } from "@/shared/hooks/useSort";
import parsePromise from '@/shared/utils/parsePromise';
import clsx from "clsx";
import { getSessionColumns, getSessionStatusLevel } from "../constants/columns";
import {
  handleContinueDoraGapAssessment,
  handleContinueFairAssessment,
  handleContinueMultiVendorAssessment,
  handleContinuePolicyManagement,
  handleContinueQuestionnaire,
  handleContinueRiskAssessment,
  handleContinueSoc2GapAssessment,
} from '../use-cases/agent-session.use-case';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(relativeTime);

const AgentSessionsHistory = () => {
  const { data: teamsData, dataUpdatedAt: teamsDataUpdatedAt } = useGetTeamsListQuery({ isEnabled: true });

  const [filteredAgentSessions, setFilteredAgentSessions] = useState<
    AgentSessions[]
  >([]);
  const [isAgentSessionsResuming, setIsAgentSessionsResuming] = useState<
    string | null
  >(null);
  // const [isAgentSessionsResuming, setIsAgentSessionsResuming] = useState<string | null>(null);
  // new state for tracking loading state per row
  const [loadingRows, setLoadingRows] = useState<Record<string, boolean>>({});
  const [isTabLoading, setIsTabLoading] = useState(true);
  const [selectedTab, setSelectedTab] = useState<AgentSessionType>(AgentSessionType.RISK_ASSESSMENT);
  // Track which tab is currently being loaded to prevent race conditions
  const latestTabRef = useRef<AgentSessionType>(AgentSessionType.RISK_ASSESSMENT);
  const [isPaginationLoading, setIsPaginationLoading] = useState(false);
  const [openDropdowns, setOpenDropdowns] = useState<Set<string>>(new Set());

  //  refs to track data loading and visibility
  const lastVisibilityChangeRef = useRef<number>(Date.now());
  const wasHiddenRef = useRef(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const navigate = useNavigate();

  const member = useLoggedInMember();

  const columns = useMemo(() =>
    getSessionColumns(selectedTab, member?.member_id),
    [selectedTab, member?.member_id]
  );



  // Cache for sessions data per tab
  const [sessionsCache, setSessionsCache] = useState<Partial<Record<AgentSessionType, any[]>>>({});
  // Local state for sessions data (current tab)
  const [sessionData, setSessionData] = useState<{
    sessions: any[];
  }>({
    sessions: [],
  });

  useEffect(() => {
    if (teamsDataUpdatedAt && sessionData) {
      setSessionData(tableSessions => {
        return {
          ...tableSessions,
          sessions: tableSessions.sessions?.map(x => {
            return {
              ...x, created_by: teamsData?.memberIdNameMapping?.[x.created_by_id as string] || ""
            }
          })
        }
      });
    }
  }, [teamsDataUpdatedAt, sessionData.sessions.length]);

  const abortControllerRef = useRef<AbortController | null>(null);

  // effect to track visibility changes
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // If we're becoming visible and we were previously hidden
        if (wasHiddenRef.current) {
          wasHiddenRef.current = false;
        }
      } else {
        // We're being hidden
        wasHiddenRef.current = true;
        lastVisibilityChangeRef.current = Date.now();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);


  // Query with local state management and caching
  const { isFetching: isAgentSessionsLoading } = useQuery({
    queryKey: ["agent-sessions", selectedTab],
    queryFn: async () => {
      const thisTab = selectedTab;
      latestTabRef.current = thisTab;
      try {
        const filters = {
          session_type: selectedTab === AgentSessionType.SOC2_GAP
            ? [
              AgentSessionType.SOC2_GAP,
              AgentSessionType.SOC2_GAP_TYPE1,
              AgentSessionType.SOC2_GAP_TYPE2,
              AgentSessionType.GAP_CUSTOM_FRAMEWORK,
              AgentSessionType.NIST_AI_RMF,
              AgentSessionType.RISK_ASSESSMENT
            ]
            : selectedTab === AgentSessionType.RISK_ASSESSMENT
              ? [
                AgentSessionType.FAIR_ASSESSMENT,
              ]
              : [selectedTab]
        };

        const data = await getPaginatedAgentSessions({
          page_number: 1,
          page_size: 1000,
          filters: filters,
          signal: abortControllerRef.current?.signal
        });

        const sessionsArr = Array.isArray(data?.data.sessions) ? data.data.sessions : [];
        const sessions = sessionsArr.map((row: AgentSession) => ({
          ...row,
          key: row.id,
          id: row.id,
          status_level: getSessionStatusLevel(row.status),
          created_by_id: row.created_by,
          created_by: teamsData?.memberIdNameMapping?.[row.created_by] || "",
        }));

        // Only update session data if this is the latest tab
        if (latestTabRef.current === thisTab) {
          if (sessions.length > 0) {
            setSessionData({ sessions });
            setSessionsCache(prev => ({ ...prev, [selectedTab]: sessions }));
          } else {
            resetSessionData();
          }
          setIsTabLoading(false);
          setIsPaginationLoading(false);
        }
        return sessions;
      } catch (e) {
        if (latestTabRef.current === thisTab) {
          setIsTabLoading(false);
          setIsPaginationLoading(false);
        }
        return [];
      }
    },
    refetchInterval: 60000,
    refetchOnWindowFocus: false // to prevent refetching when window regains focus
  });

  const { paginationState, setPaginationState, resetPageNumber } = usePagination();

  const { searchTerm, setSearchTerm, filteredData: searchFilteredData } = useSearch<any>(sessionData.sessions || [], {
    searchBy: (agentSessionDetails: AgentSession) => {
      const values: string[] = [];
      if (agentSessionDetails?.name) values.push(agentSessionDetails.name);
      if (agentSessionDetails?.created_by) values.push(agentSessionDetails.created_by);
      switch (agentSessionDetails?.type) {
        case AgentSessionType.RISK_ASSESSMENT:
        case AgentSessionType.SOC2_GAP:
        case AgentSessionType.DORA_GAP:
          values.push(frameworkTypeLabels[agentSessionDetails.type] || '');
          break;
        case AgentSessionType.MULTI_FILE_VENDOR_ASSESSMENT:
          values.push(agentSessionDetails.multi_file_vendor_assessment?.vendor_name || '');
          break;
        case AgentSessionType.QA_FILLER:
          values.push(agentSessionDetails.name);
          break;
        case AgentSessionType.POLICY_MANAGEMENT:
          values.push(agentSessionDetails.policy_management?.documentation_name || '');
          break;
        default:
          break;
      }

      return values;
    },
    resetPageNumber
  });

  const totalSessionsCount = searchFilteredData?.length ?? 0;
  const totalPages = Math.ceil(totalSessionsCount / paginationState.rowsPerPage);

  const { sortedData, setSortModel } = useSort<any>(
    searchFilteredData || [],
    { column: "", direction: "ascending" },
    { paginationState }
  );

  const paginatedSessionsList = useMemo(() => {
    const start = (paginationState.currentPage - 1) * paginationState.rowsPerPage;
    const end = paginationState.currentPage * paginationState.rowsPerPage;
    return sortedData?.slice(start, end) ?? [];
  }, [sortedData, paginationState.currentPage, paginationState.rowsPerPage]);


  const handleRiskAssessment = useCallback(
    async (session: RiskAgentSession, steps: AgentSessionStep[]) => {
      const [error] = await parsePromise(
        handleContinueRiskAssessment({ session, steps })
      );
      if (error) {
        console.error(error);
      }
    },
    []
  );

  const handleFairAssessment = useCallback(
    async (session: FairAgentSession, steps: AgentSessionStep[]) => {
      const [error] = await parsePromise(
        handleContinueFairAssessment({ session, steps })
      );
    },
    []
  );

  const handleGapAssessment = useCallback(
    async (
      session:
        | DoraAgentSession
        | SOC2GapAgentSession
        | SOC2Type2NewGapAgentSession
        | SOC2Type1GapAgentSession
        | CustomGapAgentSession,
      steps: AgentSessionStep[]
    ) => {
      try {
        switch (session.type) {
          case AgentSessionType.SOC2_GAP:
          case AgentSessionType.SOC2_GAP_TYPE1:
          case AgentSessionType.SOC2_GAP_TYPE2:
          case AgentSessionType.GAP_CUSTOM_FRAMEWORK:
            await handleContinueCustomizeControls(steps, session)
            await handleContinueSoc2GapAssessment({
              session,
              steps,
            });
            break;
          case AgentSessionType.DORA_GAP:
            await handleContinueDoraGapAssessment({
              session,
              steps,
            });
            break;
          default:
            throw new Error("Invalid session type for gap assessment");
        }
      } catch (error) {
        console.error(error);
      }
    },
    []
  );

  const handleMultiVendorAssessment = useCallback(
    async (session: MultiVendorAgentSession, steps: AgentSessionStep[]) => {
      await new Promise((resolve) => setTimeout(resolve, 100));
      const [error] = await parsePromise(
        handleContinueMultiVendorAssessment({ session, steps })
      );
      await handleContinueCustomizeControls(steps, session)

      if (error) {
        console.error(error);
      }
    },
    []
  );

  const handleContinueQaFiller = useCallback(
    async ({
      session,
      steps,
    }: {
      session: QaAgentSession;
      steps: AgentSessionStep[];
    }) => {
      const [error] = await parsePromise(
        handleContinueQuestionnaire({ session, steps })
      );

      if (error) {
        console.error(error);
      }
    },
    []
  );



  const handleDownloadReport = useCallback(
    async (session: any, fileFormat?: string) => {
      if (
        isAgentSessionsLoading ||
        !!isAgentSessionsResuming ||
        loadingRows[session.id]
      ) {
        return;
      }
      // loading spinner
      setLoadingRows((prev) => ({ ...prev, [session.id]: true }));

      addNotification({
        type: "info",
        title: "Preparing to download",
        message: "Your request to download a report is being processed.",
      });
      const response = await getAgentSession(session.id);
      if (!response || !response.data) {
        throw new Error('No session data');
      }
      const { session: sessionData, steps } = response.data;
      // Await store update before proceeding (if needed)
      await parsePromise(loadSessionResponseDataIntoStore(sessionData, steps));
      // Pass session and steps directly to avoid store timing issues
      await parsePromise(generateAndDownloadSessionReport(session.id, fileFormat, { steps }));

      // loading spinner cleanup
      setLoadingRows((prev) => ({ ...prev, [session.id]: false }));
    },
    [isAgentSessionsLoading, isAgentSessionsResuming, loadingRows]
  );

  // Render function for download options based on session type
  const renderDownloadOptions = useCallback((session: any) => {
    if (session.type === AgentSessionType.FAIR_ASSESSMENT) {
      return (
        <>
          <DropdownItem
            key="download-pdf"
            onClick={(e) => {
              handleDownloadReport(session, 'pdf')
            }}
            startContent={
              <Download className="h-4 w-4 text-[#444444]" />
            }
            className="text-[#444444]"
          >
            Download as PDF File
          </DropdownItem>
          <DropdownItem
            key="download-docx"
            onClick={(e) => {
              handleDownloadReport(session, 'docx')
            }}
            startContent={
              <Download className="h-4 w-4 text-[#444444]" />
            }
            className="text-[#444444]"
          >
            Download as Word File
          </DropdownItem>
        </>
      );
    }
    if (session.type === AgentSessionType.MULTI_FILE_VENDOR_ASSESSMENT) {
      return (
        <>
          <DropdownItem
            key="download-summary"
            onClick={(e) => {
              handleDownloadReport(session, 'summary_pdf')
            }}
            startContent={
              <Download className="h-4 w-4 text-[#444444]" />
            }
            className="text-[#444444]"
          >
            Download Summary
          </DropdownItem>
          <DropdownItem
            key="download-full-report"
            onClick={(e) => {
              handleDownloadReport(session)
            }}
            startContent={
              <Download className="h-4 w-4 text-[#444444]" />
            }
            className="text-[#444444]"
          >
            Download Full Report
          </DropdownItem>
        </>
      );
    }
    // Default download option for all other session types
    return (
      <DropdownItem
        key="download"
        onClick={(e) => {
          handleDownloadReport(session)
        }}
        startContent={
          <Download className="h-4 w-4 text-[#444444]" />
        }
        className="text-[#444444]"
      >
        Download Report
      </DropdownItem>
    );
  }, [handleDownloadReport]);

  // Helper function to get disabled keys based on session type and download readiness
  const getDisabledDownloadKeys = useCallback((session: any, isReadyForDownload: boolean) => {
    if (!isReadyForDownload) {
      if (session.type === AgentSessionType.FAIR_ASSESSMENT) {
        return ["download-pdf", "download-docx"];
      }
      if (session.type === AgentSessionType.MULTI_FILE_VENDOR_ASSESSMENT) {
        return ["download-summary", "download-full-report"];
      }
      return ["download"];
    }
    return [];
  }, []);

  const loadSessionResponseDataIntoStore = useCallback(
    async (session: AgentSession, steps: AgentSessionStep[]) => {
      try {
        switch (session.type) {
          case AgentSessionType.RISK_ASSESSMENT:
          case AgentSessionType.NIST_AI_RMF:
            await handleRiskAssessment(session, steps);
            break;
          case AgentSessionType.FAIR_ASSESSMENT:
            await handleFairAssessment(session, steps);
            break;
          case AgentSessionType.QA_FILLER:
            await handleContinueQaFiller({ session, steps });
            break;
          case AgentSessionType.SOC2_GAP:
          case AgentSessionType.SOC2_GAP_TYPE1:
          case AgentSessionType.SOC2_GAP_TYPE2:
          case AgentSessionType.DORA_GAP:
          case AgentSessionType.GAP_CUSTOM_FRAMEWORK:
            await handleGapAssessment(session, steps);
            break;
          case AgentSessionType.MULTI_FILE_VENDOR_ASSESSMENT:
            await handleMultiVendorAssessment(session, steps);
            break;
          case AgentSessionType.POLICY_MANAGEMENT:
            await handleContinuePolicyManagement({ session, steps });
            break;
          default:
            throw new Error("Invalid session type");
        }
      } catch (error) {
        console.error(error);
        addNotification({
          type: "error",
          title: "Error",
          message: "Failed to continue session. Please try again.",
        });
      }
    },
    [
      handleRiskAssessment,
      handleContinueQaFiller,
      handleFairAssessment,
      handleGapAssessment,
      handleMultiVendorAssessment,
      handleContinuePolicyManagement,
    ]
  );

  const handleSessionContinuation = useCallback(
    async (row: any) => {
      if (
        isAgentSessionsLoading ||
        !!isAgentSessionsResuming ||
        loadingRows[row.id]
      ) {
        return;
      }

      setLoadingRows((prev) => ({ ...prev, [row.id]: true }));
      setIsAgentSessionsResuming(row.id);

      try {
        // Fetch session details (with steps) on demand using direct API call
        // return ;
        const response = await getAgentSession(row.id);
        if (!response || !response.data) {
          throw new Error('No session data');
        }
        const { session, steps } = response.data;
        await loadSessionResponseDataIntoStore(session, steps);
        navigate(`/${ROUTES.AGENT}/${row.id}`);
      } catch (error) {
        setLoadingRows((prev) => ({ ...prev, [row.id]: false }));
        setIsAgentSessionsResuming(null);
        addNotification({
          type: "error",
          title: "Error",
          message: "Failed to load session details. Please try again.",
        });
        return;
      }
      setLoadingRows((prev) => ({ ...prev, [row.id]: false }));
      setIsAgentSessionsResuming(null);
    },
    [
      navigate,
      loadSessionResponseDataIntoStore,
      isAgentSessionsLoading,
      isAgentSessionsResuming,
      loadingRows,
    ]
  );
  // Debounced function to close dropdowns
  const closeDropdownsDebounced = useCallback(
    throttle(() => {
      setOpenDropdowns(new Set());
    }, 100),
    []
  );

  useEffect(() => {
    const handleWheel = () => {
      closeDropdownsDebounced();
    };

    const handleScroll = () => {
      closeDropdownsDebounced();
    };

    const timeoutId = setTimeout(() => {
      const tableElement = document.getElementById('one');

      if (tableElement) {
        tableElement.addEventListener('wheel', handleWheel, { passive: true });
        tableElement.addEventListener('scroll', handleScroll, { passive: true });
      }
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      const tableElement = document.getElementById('one');
      if (tableElement) {
        tableElement.removeEventListener('wheel', handleWheel);
        tableElement.removeEventListener('scroll', handleScroll);
      }
    };
  }, [closeDropdownsDebounced]);

  const handleSessionRowClick = useCallback(
    async ({ row }: ZTableRowParams<AgentSessions>) => {

      await handleSessionContinuation(row);
    },
    [handleSessionContinuation]
  );



  const handleTabChange = (key: React.Key) => {
    // Don't do anything if the tab is already selected
    if (key === selectedTab) {
      return;
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    // Always set session data to cached (or empty) and set loader only if no cache
    const cachedSessions = sessionsCache[key as AgentSessionType];
    setSessionData({ sessions: cachedSessions && cachedSessions.length > 0 ? cachedSessions : [] });
    setIsTabLoading(!(cachedSessions && cachedSessions.length > 0));
    setSelectedTab(key as AgentSessionType);
  };

  const resetSessionData = () => {
    setSessionData({
      sessions: [],
    });
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: <to reset the page number when the tab changes>
  useEffect(() => {
    // Reset pagination and search when tab changes (do not clear sessionData)
    setCurrentPage(1);
    resetPageNumber();
    setSearchTerm('');
    setFilteredAgentSessions([]);
    setLoadingRows({});
  }, [selectedTab]);

  // Only cleanup the abort controller on unmount if it exists
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
  }, []);

  const handleSearch = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  }, [sessionData.sessions]);

  return (
    <ErrorBoundary fallback={null}>
      <div className="w-full h-full flex flex-col">
        <div className="flex items-center pb-4 justify-between min-h-[44px] max-h-[44px]">
          <div className="flex gap-4 items-center">
            <ZBreadCrumbs
              items={[{ title: "Sessions", href: `/${ROUTES.AGENT}` }]}
            />
          </div>
          <ZButton
            disabled={isAgentSessionsLoading || !!isAgentSessionsResuming}
            onClick={() => navigate(`/${ROUTES.AGENT}`, { replace: false })}
            className="px-3 py-1 h-auto"
          >
            Start New Session
          </ZButton>
        </div>
        <div className="h-full w-full overflow-hidden rounded-lg flex flex-col">
          <div className="px-6 py-4 flex flex-col bg-white gap-2">
            {/* Below is the code for sessions header */}
            <div className="flex items-center grow justify-between w-full">
              <div className="flex items-center grow flex-1 gap-2">
                <p className="text-sm font-medium leading-5 text-[#171717]">
                  Sessions
                </p>

              </div>
              <div className="flex justify-center grow flex-1 items-center ">
                <div className="w-[612px]">
                  <Input
                    className="w-[480px] rounded-md bg-[#F4F4F5]"
                    placeholder={searchPlaceholderMap[selectedTab as keyof typeof searchPlaceholderMap]}
                    value={searchTerm}
                    disabled={
                      isAgentSessionsLoading || !!isAgentSessionsResuming
                    }
                    // NOTE: Use throttle as long as search is performed locally
                    // Use debounce if search is performed on the server via API
                    onChange={throttle(handleSearch, 200)}
                  />
                </div>
              </div>
              <div className="flex-1 overflow-auto relative" />
            </div>
            {/* End of the above code */}
          </div>
          <div className="grow overflow-hidden flex relative">
            <div className="flex-1 flex flex-col bg-white overflow-hidden">
              <div className="h-32px w-full pl-2">
                <Tabs
                  variant="underlined"
                  selectedKey={selectedTab}
                  classNames={{
                    tab: "rounded-none px-4 text-[12px] text-[#18181B]",
                    base: "rounded-none",
                    cursor: "rounded-none",
                    panel: "rounded-none",
                    tabContent: "rounded-none",
                    tabList: "rounded-none p-0",
                    wrapper: "rounded-none",
                  }}
                  className="border-b-1 bg-white w-158"
                  onSelectionChange={handleTabChange}
                >
                  <Tab
                    className={clsx("rounded-none", {
                      'after:content-[""] after:absolute after:bottom-0 after:left-0 after:w-full after:h-full after:bg-gradient-to-t after:from-[#E7F2FF] after:to-[#FFFFFF] after:border-b-2 after:border-b-[#006FEE]':
                        selectedTab === AgentSessionType.RISK_ASSESSMENT,
                    })}
                    key={AgentSessionType.RISK_ASSESSMENT}
                    title={
                      <div className="flex items-center gap-2 ">
                        <span className="text-[#18181B]">Risk Assessment</span>
                        {selectedTab === AgentSessionType.RISK_ASSESSMENT && !isTabLoading && (
                          <div className="p-1 bg-[#F4F4F5] px-2 rounded-lg">
                            <p className="text-[10px] text-[#52525B] leading-4 font-medium">
                              {totalSessionsCount}
                            </p>
                          </div>
                        )}
                      </div>
                    }
                  />
                  <Tab
                    className={clsx("rounded-none", {
                      'after:content-[""] after:absolute after:bottom-0 after:left-0 after:w-full after:h-full after:bg-gradient-to-t after:from-[#E7F2FF] after:to-[#FFFFFF] after:border-b-2 after:border-b-[#006FEE]':
                        selectedTab === AgentSessionType.SOC2_GAP
                    })}
                    key={AgentSessionType.SOC2_GAP}
                    title={
                      <div className="flex items-center gap-2 ">
                        <span className="text-[#18181B]">Gap Assessment</span>
                        {selectedTab === AgentSessionType.SOC2_GAP && !isTabLoading && (
                          <div className="p-1 bg-[#F4F4F5] px-2 rounded-lg">
                            <p className="text-[10px] text-[#52525B] leading-4 font-medium">
                              {totalSessionsCount}
                            </p>
                          </div>
                        )}
                      </div>
                    }
                  />
                  <Tab
                    className={clsx("rounded-none", {
                      'after:content-[""] after:absolute after:bottom-0 after:left-0 after:w-full after:h-full after:bg-gradient-to-t after:from-[#E7F2FF] after:to-[#FFFFFF] after:border-b-2 after:border-b-[#006FEE]':
                        selectedTab === AgentSessionType.MULTI_FILE_VENDOR_ASSESSMENT,
                    })}
                    key={AgentSessionType.MULTI_FILE_VENDOR_ASSESSMENT}
                    title={
                      <div className="flex items-center gap-2 ">
                        <span className="text-[#18181B]">Third-Party Risk Assessment</span>
                        {selectedTab === AgentSessionType.MULTI_FILE_VENDOR_ASSESSMENT && !isTabLoading && (
                          <div className="p-1 bg-[#F4F4F5] px-2 rounded-lg">
                            <p className="text-[10px] text-[#52525B] leading-4 font-medium">
                              {totalSessionsCount}
                            </p>
                          </div>
                        )}
                      </div>
                    }
                  />
                  <Tab
                    className={clsx("rounded-none", {
                      'after:content-[""] after:absolute after:bottom-0 after:left-0 after:w-full after:h-full after:bg-gradient-to-t after:from-[#E7F2FF] after:to-[#FFFFFF] after:border-b-2 after:border-b-[#006FEE]':
                        selectedTab === AgentSessionType.QA_FILLER,
                    })}
                    key={AgentSessionType.QA_FILLER}
                    title={
                      <div className="flex items-center gap-2 ">
                        <span className="text-[#18181B]">Security Questionnaire</span>
                        {selectedTab === AgentSessionType.QA_FILLER && !isTabLoading && (
                          <div className="p-1 bg-[#F4F4F5] px-2 rounded-lg">
                            <p className="text-[10px] text-[#52525B] leading-4 font-medium">
                              {totalSessionsCount}
                            </p>
                          </div>
                        )}
                      </div>
                    }
                  />
                  {/* enable policy management */}
                  <Tab
                    className={clsx("rounded-none", {
                      'after:content-[""] after:absolute after:bottom-0 after:left-0 after:w-full after:h-full after:bg-gradient-to-t after:from-[#E7F2FF] after:to-[#FFFFFF] after:border-b-2 after:border-b-[#006FEE]':
                        selectedTab === AgentSessionType.POLICY_MANAGEMENT,
                    })}
                    key={AgentSessionType.POLICY_MANAGEMENT}
                    title={
                      <div className="flex items-center gap-2 ">
                        <span className="text-[#18181B]">Policy Management</span>
                        {selectedTab === AgentSessionType.POLICY_MANAGEMENT && !isTabLoading && (
                          <div className="p-1 bg-[#F4F4F5] px-2 rounded-lg">
                            <p className="text-[10px] text-[#52525B] leading-4 font-medium">
                              {totalSessionsCount}
                            </p>
                          </div>
                        )}
                      </div>
                    }
                  />
                </Tabs>
              </div>
              <div id="one" className="flex-1 flex flex-col overflow-hidden">
                <ZTable<any>
                  className={cn(
                    "border-1 border-[#E4E4E7] overflow-auto scroll-auto h-full",
                  )}
                  id="agent-sessions-table"
                  layout="auto"
                  loading={isAgentSessionsLoading || isTabLoading || isPaginationLoading}
                  rows={paginatedSessionsList || []}
                  columns={[
                    ...columns,
                    {
                      type: "custom",
                      fieldName: "actions",
                      headerName: "",
                      headerClassName: "!w-[60px]",
                      renderCell: (row) => {
                        const { status, id, final_report_available } = row;
                        const isRowLoading = loadingRows[id];
                        const isReadyForDownload = !([AgentSessionStatus.PENDING, AgentSessionStatus.QUEUED, AgentSessionStatus.IN_PROGRESS, AgentSessionStatus.UNDER_REASSESSMENT, AgentSessionStatus.FORM_SENT, AgentSessionStatus.VENDOR_INPUT_NEEDED, AgentSessionStatus.EXTRACT_CONTROLS, AgentSessionStatus.REFINE_IN_PROGRESS] as string[]).includes(status);
                        return (
                          <div className="flex justify-end w-full items-center h-full min-h-[40px]">
                            {isRowLoading ? (
                              <div className="w-8 h-8 flex items-center justify-center">
                                <LoadingSpinner
                                  variant="centered"
                                  className="h-5 w-5"
                                />
                              </div>
                            ) : (
                              <Dropdown
                                isOpen={openDropdowns.has(id)}
                                onOpenChange={(isOpen) => {
                                  setOpenDropdowns(prev => {
                                    const newSet = new Set(prev);
                                    if (isOpen) {
                                      newSet.add(id);
                                    } else {
                                      newSet.delete(id);
                                    }
                                    return newSet;
                                  });
                                }}
                              >
                                <DropdownTrigger>
                                  <Button
                                    isIconOnly
                                    className="bg-transparent hover:bg-gray-100 transition-colors"
                                    isDisabled={isAgentSessionsLoading || !!isAgentSessionsResuming}
                                  >
                                    <Ellipsis className="h-5 w-5" color="#717179" />
                                  </Button>
                                </DropdownTrigger>
                                <DropdownMenu
                                  disabledKeys={getDisabledDownloadKeys(row, isReadyForDownload && final_report_available)}
                                >

                                  <DropdownItem
                                    key="continue"
                                    onClick={e => { 
                                      if (e && typeof e.stopPropagation === 'function') {
                                        e.stopPropagation(); 
                                      }
                                      /* Only trigger navigation if user selects this item, not on dropdown open */ 
                                      handleSessionContinuation(row); 
                                    }}
                                    startContent={
                                      <ArrowRight className="h-4 w-4 text-[#444444]" />
                                    }
                                    className="text-[#444444]"
                                  >
                                    {status === AgentSessionStatus.COMPLETE
                                      ? "View Session"
                                      : "Continue Session"}
                                  </DropdownItem>

                                  {renderDownloadOptions(row)}
                                </DropdownMenu>
                              </Dropdown>
                            )}
                          </div>
                        );
                      },
                    },
                  ]}
                  onSortModelChange={setSortModel}
                  isHeaderSticky={true}
                  isCompact={true}
                  removeWrapper={true}
                  getRowId={(row) => row.id}
                  tableHeaderProps={{
                    className: "shadow-none rounded-none ",

                  }}

                  tableBodyProps={{
                    emptyContent: (isAgentSessionsLoading || isTabLoading || isPaginationLoading) ? (
                      <div className="w-full py-4">
                        <Spinner />
                      </div>
                      // <div className="w-full py-4">
                      //   {[...Array(10)].map((_, index) => (
                      //     <div key={index} className="flex gap-4 w-full px-4 py-2">
                      //       <Skeleton className="w-[100%] h-12 rounded-lg" />
                      //     </div>
                      //   ))}
                      // </div>
                    ) : "No agent sessions found"
                  }}
                  onRowClick={handleSessionRowClick}
                  tableRowProps={(row, rowIndex) => ({
                    className: cn(
                      rowIndex === 0 ? "border-none" : "border-y",
                      "hover:bg-[#F2F2F2]",
                      "group",
                      "cursor-pointer",
                      row && row.id && loadingRows[row.id] && "cursor-wait",
                    ),
                  })}
                  classNames={{
                    wrapper: "!shadow-none !rounded-none",
                    base: "!shadow-none !rounded-none",
                    th: "!shadow-none !rounded-none  !bg-gradient-to-b !from-[#f5f2f4] !to-[rgb(245,242,244)]",
                    tr: "",
                  }}
                  aria-label="Agent Sessions History"
                  isSortingControlled
                />
                <ZTablePagination
                  totalPages={totalPages}
                  paginationState={paginationState}
                  setPagination={setPaginationState}
                  className="px-5 !py-3 border-t border-[#E4E4E7]"
                />
              </div>
            </div>
          </div>
        </div>
        <div className="grow overflow-hidden rounded-xl">
          <Outlet />
        </div>
      </div>
    </ErrorBoundary>
  );
};

export default AgentSessionsHistory;